import React, { useEffect, useState } from 'react';
import {
  Alert,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { fromIstWallClock, toIstWallClock } from '@/utils/cricketCalendar';
import { fixtureEditDiff } from '@/utils/cricketFixtureDiff';
import type { CricketFixture } from '@/types/matchPushes';

interface FixtureFormValues {
  team1Name: string;
  team2Name: string;
  startsAt: Dayjs;
  format?: string;
  seriesName?: string;
  matchLabel?: string;
  venue?: string;
  audienceCountries: string[];
  tracked: boolean;
  status?: 'upcoming' | 'cancelled';
}

const FORMATS = ['t20', 'odi', 'test', 't10', 'other'];
const ISO2 = /^[A-Z]{2}$/;

interface Props {
  open: boolean;
  /** The match to edit; null adds one by hand. */
  fixture: CricketFixture | null;
  onClose: () => void;
  /** Called with the saved match, or null after a delete. */
  onSaved: (fixture: CricketFixture | null) => void;
}

/**
 * Add a match by hand, or edit one. The time is entered in IST. Fields changed
 * on a Sportskeeda match are locked: the daily sync no longer overwrites them.
 */
export const FixtureFormModal: React.FC<Props> = ({ open, fixture, onClose, onSaved }) => {
  const [form] = Form.useForm<FixtureFormValues>();
  const [saving, setSaving] = useState(false);
  const editing = fixture !== null;
  const canSetStatus = fixture?.status === 'upcoming' || fixture?.status === 'cancelled';

  useEffect(() => {
    if (!open) return;
    if (fixture) {
      form.setFieldsValue({
        team1Name: fixture.team1Name,
        team2Name: fixture.team2Name,
        startsAt: toIstWallClock(fixture.startsAt),
        seriesName: fixture.seriesName ?? undefined,
        matchLabel: fixture.matchLabel ?? undefined,
        venue: fixture.venue ?? undefined,
        audienceCountries: fixture.audienceCountries,
        tracked: fixture.tracked,
        status: canSetStatus ? (fixture.status as 'upcoming' | 'cancelled') : undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ tracked: true, audienceCountries: [], format: 't20' });
    }
  }, [open, fixture, form, canSetStatus]);

  const submit = async () => {
    let values: FixtureFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // the form shows which fields to fix
    }
    setSaving(true);
    try {
      const response = fixture
        ? await adminApi.cricketCalendar.updateFixture(
            fixture.id,
            fixtureEditDiff(fixture, {
              ...values,
              status: canSetStatus ? values.status : undefined,
            })
          )
        : await adminApi.cricketCalendar.createFixture({
            team1Name: values.team1Name.trim(),
            team2Name: values.team2Name.trim(),
            startsAt: fromIstWallClock(values.startsAt),
            format: values.format,
            seriesName: values.seriesName?.trim() || undefined,
            matchLabel: values.matchLabel?.trim() || undefined,
            venue: values.venue?.trim() || undefined,
            audienceCountries: values.audienceCountries,
            tracked: values.tracked,
          });
      message.success(fixture ? 'Match saved' : 'Match added');
      onSaved(response.data);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to save the match'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!fixture) return;
    setSaving(true);
    try {
      await adminApi.cricketCalendar.deleteFixture(fixture.id);
      message.success('Match deleted');
      onSaved(null);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to delete the match'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? 'Edit match' : 'Add match by hand'}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText={editing ? 'Save' : 'Add match'}
      confirmLoading={saving}
      destroyOnClose
      width={560}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Space>
          {fixture?.source === 'manual' && (
            <Popconfirm
              title="Delete this match?"
              description="Its push plans go with it."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={remove}
            >
              <a style={{ color: '#ff4d4f' }}>Delete</a>
            </Popconfirm>
          )}
          <CancelBtn />
          <OkBtn />
        </Space>
      )}
    >
      {fixture?.source === 'sportskeeda' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="This match comes from Sportskeeda. A field you change here stays as you set it; the daily sync will not overwrite it."
          description={
            fixture.lockedFields.length ? (
              <span>
                Already locked:{' '}
                {fixture.lockedFields.map((f) => (
                  <Tag key={f}>{f}</Tag>
                ))}
              </span>
            ) : undefined
          }
        />
      )}
      <Form form={form} layout="vertical" requiredMark="optional">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="team1Name"
              label="Team 1"
              rules={[{ required: true, whitespace: true, max: 100 }]}
            >
              <Input placeholder="India" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="team2Name"
              label="Team 2"
              rules={[{ required: true, whitespace: true, max: 100 }]}
            >
              <Input placeholder="Pakistan" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="startsAt"
          label="Starts (IST)"
          rules={[{ required: true, message: 'Pick the start time' }]}
        >
          <DatePicker showTime={{ format: 'HH:mm' }} format="DD MMM YYYY HH:mm" minuteStep={5} />
        </Form.Item>
        {!editing && (
          <Form.Item name="format" label="Format">
            <Select options={FORMATS.map((f) => ({ value: f, label: f.toUpperCase() }))} />
          </Form.Item>
        )}
        <Form.Item name="seriesName" label="Series" rules={[{ max: 200 }]}>
          <Input placeholder="Indian Premier League 2027" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="matchLabel" label="Match label" rules={[{ max: 60 }]}>
              <Input placeholder="1st T20I" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="venue" label="Venue" rules={[{ max: 120 }]}>
              <Input placeholder="Christchurch" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="audienceCountries"
          label="Plays to (country codes)"
          extra="Players in these countries get this match's pushes. Use ISO-2 codes such as IN, PK, LK, BD."
          normalize={(value: string[] | undefined) =>
            (value ?? []).map((c) => c.trim().toUpperCase())
          }
          rules={[
            {
              validator: (_, value: string[] | undefined) =>
                (value ?? []).every((c) => ISO2.test(c))
                  ? Promise.resolve()
                  : Promise.reject(new Error('Use two-letter country codes')),
            },
          ]}
        >
          <Select mode="tags" tokenSeparators={[',', ' ']} placeholder="IN" open={false} />
        </Form.Item>
        <Form.Item name="tracked" label="Tracked" valuePropName="checked">
          <Switch />
        </Form.Item>
        {editing && canSetStatus && (
          <Form.Item name="status" label="Status">
            <Select
              options={[
                { value: 'upcoming', label: 'Upcoming' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
